<template>
  <div class="auth-page">
    <div class="auth-right">
      <div class="auth-card">
        <div class="auth-icon-wrap">
          <i class="bi bi-shield-lock-fill"></i>
        </div>
        <h2 class="auth-title">Verify your account</h2>
        <p class="auth-subtitle">Enter the 5-digit OTP sent to your phone.</p>

        <div v-if="error" class="auth-error"><i class="bi bi-exclamation-circle mr-2"></i>{{ error }}</div>
        <div v-if="success" class="auth-success"><i class="bi bi-check-circle mr-2"></i>{{ success }}</div>

        <form @submit.prevent="submit">
          <div class="auth-field">
            <label class="auth-label">Email address</label>
            <input v-model="form.email" type="email" class="auth-input" required />
          </div>
          <div class="auth-field">
            <label class="auth-label">OTP Code</label>
            <input
              v-model="form.otp"
              class="auth-input auth-otp"
              maxlength="5"
              minlength="5"
              required
              placeholder="· · · · ·"
              autocomplete="one-time-code"
            />
          </div>
          <button type="submit" class="auth-btn" :disabled="loading">
            <span v-if="loading" class="auth-spinner"></span>
            <span v-else>Verify Account</span>
          </button>
        </form>

        <div class="auth-divider">or</div>

        <form @submit.prevent="resend">
          <div class="auth-field">
            <label class="auth-label">Resend OTP to phone number</label>
            <input v-model="phone" type="tel" class="auth-input" placeholder="+27 ..." required />
          </div>
          <button type="submit" class="auth-btn auth-btn--outline" :disabled="resending">
            <span v-if="resending" class="auth-spinner auth-spinner--dark"></span>
            <span v-else>Resend OTP</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import authApi from '@/api/auth'

export default {
  name: 'VerifyAccountView',
  data () {
    return {
      form: { email: this.$route.query.email || '', otp: '' },
      phone: '',
      loading: false,
      resending: false,
      error: null,
      success: null
    }
  },
  methods: {
    async submit () {
      this.error = null
      this.loading = true
      try {
        await this.$store.dispatch('auth/verifyAccount', this.form)
        this.$router.push({ name: 'Dashboard' })
      } catch (err) {
        this.error = err.response?.data?.message || 'Verification failed. Check your OTP and try again.'
      } finally {
        this.loading = false
      }
    },
    async resend () {
      this.error = null
      this.resending = true
      try {
        await authApi.resendOtp({ phoneNumber: this.phone })
        this.success = 'OTP resent successfully.'
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to resend OTP.'
      } finally {
        this.resending = false
      }
    }
  }
}
</script>

<style scoped>
.auth-page { min-height: 100vh; background: #212121; display: flex; align-items: center; justify-content: center; padding: 20px; }
.auth-right { width: 100%; display: flex; justify-content: center; }
.auth-card { background: #fff; border-radius: 16px; padding: 40px 36px; width: 100%; max-width: 420px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.auth-icon-wrap { width: 56px; height: 56px; background: rgba(255,152,0,0.12); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; color: rgb(255,152,0); margin-bottom: 16px; }
.auth-title { font-size: 1.5rem; font-weight: 800; color: #212121; margin: 0 0 4px; }
.auth-subtitle { color: #9e9e9e; font-size: 0.9rem; margin-bottom: 24px; }
.auth-error { background: #fce4ec; border-left: 4px solid #ef5350; color: #b71c1c; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 16px; }
.auth-success { background: #e8f5e9; border-left: 4px solid #66bb6a; color: #1b5e20; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 16px; }
.auth-field { margin-bottom: 16px; }
.auth-label { display: block; font-size: 0.8rem; font-weight: 600; color: #424242; margin-bottom: 6px; }
.auth-input { width: 100%; border: 1.5px solid #e0e0e0; border-radius: 10px; padding: 11px 14px; font-size: 0.9rem; outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: #fafafa; color: #212121; }
.auth-input:focus { border-color: rgb(255,152,0); box-shadow: 0 0 0 3px rgba(255,152,0,0.12); background: #fff; }
.auth-otp { text-align: center; letter-spacing: 12px; font-size: 1.4rem; font-weight: 700; }
.auth-btn { width: 100%; padding: 12px; background: rgb(255,152,0); color: #fff; border: none; border-radius: 10px; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: background 0.18s, box-shadow 0.18s; display: flex; align-items: center; justify-content: center; gap: 8px; }
.auth-btn:hover:not(:disabled) { background: rgb(230,126,0); box-shadow: 0 4px 16px rgba(255,152,0,0.35); }
.auth-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.auth-btn--outline { background: transparent; color: #424242; border: 1.5px solid #e0e0e0; }
.auth-btn--outline:hover:not(:disabled) { background: #f5f5f5; box-shadow: none; }
.auth-spinner { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block; }
.auth-spinner--dark { border: 2.5px solid rgba(0,0,0,0.12); border-top-color: #424242; }
@keyframes spin { to { transform: rotate(360deg); } }
.auth-divider { text-align: center; color: #bdbdbd; font-size: 0.8rem; margin: 20px 0; position: relative; }
.auth-divider::before, .auth-divider::after { content: ''; position: absolute; top: 50%; width: 44%; height: 1px; background: #e0e0e0; }
.auth-divider::before { left: 0; } .auth-divider::after { right: 0; }
</style>
